"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"

import { useWebsocket } from "@/components/websocket/websocket-provider"
import { useAppSelector } from "@/lib/store"

import { loadActiveCall, sendCallControl } from "./load-active-call"
import type {
  ActiveCallControlAction,
  ActiveCallSnapshot,
  SystemCallSnapshot,
} from "./types"

type ActiveCallContextValue = {
  activeCall: ActiveCallSnapshot | null
  calls: ActiveCallSnapshot[]
  systemCall: ActiveCallSnapshot | null
  selectedCallId: string | null
  selectCall: (callId: string) => void
  loading: boolean
  error: string | null
  controlPending: ActiveCallControlAction | null
  controlError: string | null
  refreshActiveCall: (options?: { clearIfEmpty?: boolean }) => Promise<ActiveCallSnapshot | null>
  runCallControl: (
    action: ActiveCallControlAction,
    options?: { digit?: string; destination_type?: string; destination_id?: string; destination_number?: string }
  ) => Promise<void>
}

const ActiveCallContext = createContext<ActiveCallContextValue | null>(null)

function activeCallLog(...args: unknown[]): void {
  if (typeof window === "undefined") return
  console.log("[active-call]", new Date().toISOString(), ...args)
}

function callKey(call: ActiveCallSnapshot): string | null {
  return call.call_id ?? call.request_ulid ?? null
}

function pickSelectedCall(
  calls: ActiveCallSnapshot[],
  systemCall: ActiveCallSnapshot | null,
  preferredId: string | null
): ActiveCallSnapshot | null {
  if (preferredId) {
    const match = calls.find((call) => callKey(call) === preferredId)
    if (match) return match
    if (systemCall && callKey(systemCall) === preferredId) return systemCall
  }
  if (calls.length > 0) return calls[0]
  return systemCall
}

export function ActiveCallProvider({ children }: { children: ReactNode }) {
  const { adoptRealtimeCursor, registerRealtimeScope } = useWebsocket()
  const hasPhoneSystem = useAppSelector(
    (state) => state.sessionBootstrap.data?.workspace.has_phone_system ?? false
  )
  const [activeCall, setActiveCall] = useState<ActiveCallSnapshot | null>(null)
  const [calls, setCalls] = useState<ActiveCallSnapshot[]>([])
  const [systemCall, setSystemCall] = useState<ActiveCallSnapshot | null>(null)
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [controlPending, setControlPending] = useState<ActiveCallControlAction | null>(
    null
  )
  const [controlError, setControlError] = useState<string | null>(null)
  const pendingActionRef = useRef<ActiveCallControlAction | null>(null)

  const selectedCallIdRef = useRef<string | null>(null)
  const hasDisplayableCallRef = useRef(false)
  const activeCallRef = useRef<ActiveCallSnapshot | null>(null)

  useEffect(() => {
    activeCallRef.current = activeCall
  }, [activeCall])

  const refreshActiveCall = useCallback(async (options?: { clearIfEmpty?: boolean }): Promise<ActiveCallSnapshot | null> => {
    const clearIfEmpty = options?.clearIfEmpty !== false
    if (!hasPhoneSystem) {
      setActiveCall(null)
      setCalls([])
      setSystemCall(null)
      setSelectedCallId(null)
      selectedCallIdRef.current = null
      hasDisplayableCallRef.current = false
      setLoading(false)
      setError(null)
      activeCallLog("refresh skipped — workspace has no phone system")
      return null
    }

    const isInitialLoad = !hasDisplayableCallRef.current
    if (isInitialLoad) {
      setLoading(true)
    }
    setError(null)
    try {
      const data = await loadActiveCall()
      adoptRealtimeCursor(data.realtime_state === "ok" ? data.realtime_cursor : null)
      const nextSystemCall = data.system_call
      const nextCalls = data.calls ?? []
      let selected = pickSelectedCall(
        nextCalls,
        nextSystemCall,
        selectedCallIdRef.current
      )
      const stickyId = selectedCallIdRef.current
      if (
        nextCalls.length > 0 &&
        stickyId &&
        nextCalls[0].call_mode === "list_dial" &&
        !nextCalls.some((call) => callKey(call) === stickyId)
      ) {
        selected = nextCalls[0]
      }
      const key = selected ? callKey(selected) : null

      if (selected) {
        setCalls(nextCalls)
        setSystemCall(nextSystemCall)
        setActiveCall(selected)
        setSelectedCallId(key)
        selectedCallIdRef.current = key
        hasDisplayableCallRef.current = true
      } else if (clearIfEmpty) {
        setCalls([])
        setSystemCall(null)
        setActiveCall(null)
        setSelectedCallId(null)
        selectedCallIdRef.current = null
        hasDisplayableCallRef.current = false
      }

      if (pendingActionRef.current === null) {
        setControlPending(null)
        setControlError(null)
      }

      activeCallLog("refresh", {
        active_call: selected,
        calls: nextCalls,
        system_call: nextSystemCall,
        clearIfEmpty,
        preserved: !selected && !clearIfEmpty,
      })
      return selected
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      setError(message)
      activeCallLog("refresh failed", message)
      return hasDisplayableCallRef.current ? activeCallRef.current : null
    } finally {
      if (isInitialLoad) {
        setLoading(false)
      }
    }
  }, [adoptRealtimeCursor, hasPhoneSystem])

  const selectCall = useCallback((callId: string) => {
    selectedCallIdRef.current = callId
    setSelectedCallId(callId)
    setCalls((currentCalls) => {
      setSystemCall((currentSystem) => {
        const selected = pickSelectedCall(currentCalls, currentSystem, callId)
        setActiveCall(selected)
        return currentSystem
      })
      return currentCalls
    })
  }, [])

  const runCallControl = useCallback(
    async (
      action: ActiveCallControlAction,
      options?: { digit?: string; destination_type?: string; destination_id?: string; destination_number?: string }
    ) => {
      const target = activeCall
      const callId = target?.call_id
      if (!callId) {
        setControlError("No call selected for control")
        return
      }
      if (!target.actions_enabled?.[action]) {
        setControlError("Call controls are not available yet")
        return
      }

      setControlPending(action)
      setControlError(null)
      pendingActionRef.current = action
      try {
        await sendCallControl({
          call_id: callId,
          action,
          digit: options?.digit,
          destination_type: options?.destination_type,
          destination_id: options?.destination_id,
          destination_number: options?.destination_number,
        })
        setControlPending(null)
        pendingActionRef.current = null
        activeCallLog("control accepted", { action, callId })
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e)
        setControlError(message)
        setControlPending(null)
        pendingActionRef.current = null
        activeCallLog("control failed", message)
      }
    },
    [activeCall]
  )

  useEffect(() => {
    void refreshActiveCall()
  }, [refreshActiveCall])

  useEffect(() => {
    if (!hasPhoneSystem) return
    return registerRealtimeScope("active_call", () => {
      void refreshActiveCall()
    })
  }, [hasPhoneSystem, refreshActiveCall, registerRealtimeScope])

  const value = useMemo<ActiveCallContextValue>(
    () => ({
      activeCall,
      calls,
      systemCall,
      selectedCallId,
      selectCall,
      loading,
      error,
      controlPending,
      controlError,
      refreshActiveCall,
      runCallControl,
    }),
    [
      activeCall,
      calls,
      systemCall,
      selectedCallId,
      selectCall,
      loading,
      error,
      controlPending,
      controlError,
      refreshActiveCall,
      runCallControl,
    ]
  )

  return (
    <ActiveCallContext.Provider value={value}>
      {children}
    </ActiveCallContext.Provider>
  )
}

export function useActiveCall(): ActiveCallContextValue {
  const value = useContext(ActiveCallContext)
  if (!value) {
    throw new Error("useActiveCall must be used inside ActiveCallProvider")
  }
  return value
}

export type { ActiveCallSnapshot, SystemCallSnapshot }
